"use client";

import { useState } from "react";
import Link from "next/link";
import Footer from "@/components/shared/Footer";
import { isLensLaunchEnabled } from "@/lib/lens-scope";
import LensOutOfScopeNotice from "./LensOutOfScopeNotice";

/* ── Stripe Payment Links ────────────────────────────────────────────────────
   Replace these with your actual Stripe Payment Link URLs.
   Create them in: Stripe Dashboard → Payment Links → + New
   ─────────────────────────────────────────────────────────────────────────── */
const PAYMENT_LINKS = {
  starter: "https://buy.stripe.com/3cI3cugPC0EhcNCf3RdAk06",
  solo: "https://buy.stripe.com/4gM3cudDqfzb00Qg7VdAk02",
  professional: "https://buy.stripe.com/6oUfZgeHu0EhdRG7BpdAk03",
  business: "https://buy.stripe.com/8x214mfLyfzbfZO3l9dAk04",
  enterprise: "https://buy.stripe.com/eVqeVcdDq9aNaFu5thdAk05",
};

const ANNUAL_PRICES: Record<string, number> = { starter: 290, solo: 1490, professional: 3490, business: 7490, enterprise: 14990 };

const plans = [
  {
    tier: "starter",
    name: "Starter",
    price: 29,
    annualPrice: 290,
    period: "/month",
    desc: "For freelancers and consultants who need basic PII scanning and redaction.",
    devices: 1,
    features: [
      "File & Content Search",
      "6 PII Detection Filters",
      "Text Redaction",
      "Dashboard & Analytics",
      "Duplicate Detection",
      "Bookmarks & Favourites",
    ],
    cta: "Get Starter",
    popular: false,
  },
  {
    tier: "solo",
    name: "Solo",
    price: 149,
    annualPrice: 1490,
    period: "/month",
    desc: "For compliance officers who need full PII detection with AI analysis and compliance tools.",
    devices: 1,
    features: [
      "Everything in Starter, plus:",
      "All 15+ PII Detection Filters",
      "Custom Patterns & Profiles",
      "Watch Folder Monitoring",
      "Compliance Center (45+ Frameworks)",
      "PDF Audit Certificates",
      "AI Analysis (Local via Ollama)",
      "Regex Playground",
    ],
    cta: "Get Solo",
    popular: false,
  },
  {
    tier: "professional",
    name: "Professional",
    price: 349,
    annualPrice: 3490,
    period: "/month",
    desc: "For power users who need batch processing, scheduled scans, and document comparison.",
    devices: 3,
    features: [
      "Everything in Solo, plus:",
      "Batch Processing",
      "Document Compare",
      "Scan Statistics & History",
      "Scheduled Scans",
      "3 Devices",
    ],
    cta: "Get Professional",
    popular: true,
  },
  {
    tier: "business",
    name: "Business",
    price: 749,
    annualPrice: 7490,
    period: "/month",
    desc: "For teams who need API, DSAR, breach simulation, and full compliance automation.",
    devices: 10,
    features: [
      "Everything in Professional, plus:",
      "Image Redaction (Canvas-based)",
      "REST API Server",
      "DSAR Automation & Data Subject Registry",
      "Breach Simulation & Response Planner",
      "Compliance Reports (SOC 2, ISO 27001, NIST, GDPR)",
      "DPIA Wizard & ROPA Auto-Generation",
      "Gap Analysis & Cross-Reference Scanning",
      "Incident Response & Remediation Tracking",
      "Compliance Calendar & Data Retention Policies",
      "One-Click Audit-Ready Compliance Package",
      "10 Seats / Devices",
    ],
    cta: "Get Business",
    popular: false,
  },
];

const enterprise = {
  name: "Enterprise",
  price: 1499,
  annualPrice: 14990,
  period: "/month",
  desc: "For organisations that need RBAC, executive dashboards, GenAI protection, and full automation.",
  devices: 25,
  features: [
    "Everything in Business, plus:",
    "Audio Transcription & PII Detection",
    "RBAC & Team Management (25 Seats)",
    "Executive Dashboard with Org Risk Score",
    "GenAI Data Protection (Clipboard Guard)",
    "White-Label Audit Certificates",
    "Webhook Notifications",
    "Encrypted Audit Exports",
    "Multi-Folder Scanning",
    "Scheduled PDF Reports",
  ],
};

const compliance = [
  { name: "GDPR", desc: "EU General Data Protection Regulation" },
  { name: "HIPAA", desc: "Health Insurance Portability & Accountability" },
  { name: "PCI-DSS", desc: "Payment Card Industry Data Security" },
  { name: "SOX", desc: "Sarbanes-Oxley Financial Compliance" },
  { name: "EU AI Act", desc: "European AI Regulation" },
  { name: "CCPA", desc: "California Consumer Privacy Act" },
  { name: "DORA", desc: "Digital Operational Resilience Act" },
  { name: "ISO 27001", desc: "Information Security Management" },
  { name: "NIST CSF", desc: "Cybersecurity Framework" },
  { name: "SOC 2", desc: "Service Organization Controls" },
];

const piiTypes = [
  "Email Addresses", "Phone Numbers", "UK National Insurance", "US Social Security",
  "Credit Card Numbers", "IBAN / Bank Accounts", "UK Postcodes", "Dates of Birth",
  "IP Addresses", "Passport Numbers", "Driving Licence Numbers", "NHS Numbers",
  "VAT Numbers", "SWIFT/BIC Codes", "Vehicle Registration", "Full Names",
  "Physical Addresses", "Custom Patterns",
];

const faqs = [
  {
    q: "Is my data sent to the cloud?",
    a: "No. Aditya Lens processes everything 100% locally on your machine. No files, no PII, no analytics ever leave your computer. AI analysis uses Ollama running on localhost.",
  },
  {
    q: "How does licensing work?",
    a: "After purchase, you receive an AL-XXXX-XXXX-XXXX-XXXX license key by email. Paste it into Settings → License in the app. The app validates once online, then works fully offline.",
  },
  {
    q: "Can I use one license on multiple devices?",
    a: "Starter: 1 device. Solo: 1 device. Professional: 3 devices. Business: 10 devices. Enterprise: 25 devices. Contact support to deactivate an old device if you need to switch.",
  },
  {
    q: "What file types are supported?",
    a: "TXT, PDF, DOCX, CSV, JSON, HTML, XML, images (PNG, JPG, BMP, TIFF for image redaction), and audio files (WAV, MP3, FLAC for Enterprise tier transcription).",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Cancel from the app via Settings → Manage Billing, or through the Stripe customer portal. Your license deactivates at the end of the billing period.",
  },
  {
    q: "What happens if my payment fails?",
    a: "We retry 3 times. You'll get an email to update your card. After 3 failed attempts, your license is deactivated. Resubscribe anytime to get a new key.",
  },
];

export default function LensPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [annual, setAnnual] = useState(false);

  // Aditya Lens is out of scope for the current private pilot — render a notice
  // instead of the (retained) product landing page unless explicitly enabled.
  if (!isLensLaunchEnabled()) {
    return <LensOutOfScopeNotice />;
  }

  return (
    <>
      <div style={{ minHeight: "100vh", background: "var(--background, #08081a)", color: "var(--foreground, #e6edf3)" }}>

        {/* ── Nav ──────────────────────────────────────────────────────── */}
        <nav style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1rem 2rem", borderBottom: "1px solid rgba(255,255,255,0.06)", maxWidth: 1280, margin: "0 auto" }}>
          <Link href="/" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: "1.3rem", fontWeight: 800, color: "#c4a265", letterSpacing: 1 }}>ADITYA LENS</span>
            <span style={{ fontSize: ".7rem", color: "#6b6b8a", border: "1px solid #2a2a3e", padding: "2px 8px", borderRadius: 4 }}>by Aditya Labs</span>
          </Link>
          <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
            <a href="#pricing" style={{ color: "#c4c4d4", textDecoration: "none", fontSize: ".9rem" }}>Pricing</a>
            <a href="#features" style={{ color: "#c4c4d4", textDecoration: "none", fontSize: ".9rem" }}>Features</a>
            <a href="#faq" style={{ color: "#c4c4d4", textDecoration: "none", fontSize: ".9rem" }}>FAQ</a>
            <a href="https://adityalabs.ai/lens/download" style={{ background: "linear-gradient(135deg, #c4a265, #d4b878)", color: "#08081a", padding: "8px 20px", borderRadius: 8, fontWeight: 700, fontSize: ".85rem", textDecoration: "none" }}>
              Download
            </a>
          </div>
        </nav>

        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <section style={{ textAlign: "center", padding: "5rem 2rem 3rem", maxWidth: 900, margin: "0 auto" }}>
          <div style={{ display: "inline-block", background: "rgba(196,162,101,0.1)", border: "1px solid rgba(196,162,101,0.3)", borderRadius: 20, padding: "4px 16px", fontSize: ".75rem", color: "#c4a265", fontWeight: 600, marginBottom: 24 }}>
            14-Day Free Trial &middot; No Credit Card &middot; 100% Local &middot; Your Data Never Leaves Your Machine
          </div>
          <h1 style={{ fontSize: "3.2rem", fontWeight: 800, lineHeight: 1.1, margin: "0 0 1.5rem", background: "linear-gradient(135deg, #ffffff 0%, #c4a265 50%, #d4b878 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            Enterprise PII Detection,<br />Redaction & Compliance
          </h1>
          <p style={{ fontSize: "1.15rem", color: "#8b8ba0", lineHeight: 1.7, maxWidth: 650, margin: "0 auto 2rem" }}>
            Scan documents for personal data. Redact text and images. Generate compliance reports.
            All processing happens on your Windows PC — nothing is ever uploaded.
          </p>
          <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
            <a href="#pricing" style={{ background: "linear-gradient(135deg, #c4a265, #d4b878)", color: "#08081a", padding: "14px 32px", borderRadius: 10, fontWeight: 700, fontSize: "1rem", textDecoration: "none" }}>
              View Plans
            </a>
            <a href="https://adityalabs.ai/lens/download" style={{ border: "1px solid #c4a265", color: "#c4a265", padding: "14px 32px", borderRadius: 10, fontWeight: 700, fontSize: "1rem", textDecoration: "none", background: "transparent" }}>
              Download Free Trial
            </a>
          </div>
        </section>

        {/* ── Stats Bar ────────────────────────────────────────────────── */}
        <section style={{ display: "flex", justifyContent: "center", gap: 48, padding: "2rem", flexWrap: "wrap" }}>
          {[
            { n: "18", label: "PII Patterns" },
            { n: "185", label: "Test Cases, 0 Leaks" },
            { n: "7", label: "Compliance Frameworks" },
            { n: "100%", label: "Local Processing" },
          ].map((s) => (
            <div key={s.label} style={{ textAlign: "center" }}>
              <div style={{ fontSize: "2rem", fontWeight: 800, color: "#c4a265" }}>{s.n}</div>
              <div style={{ fontSize: ".8rem", color: "#6b6b8a" }}>{s.label}</div>
            </div>
          ))}
        </section>

        {/* ── PII Types Grid ───────────────────────────────────────────── */}
        <section id="features" style={{ maxWidth: 1100, margin: "3rem auto", padding: "0 2rem" }}>
          <h2 style={{ textAlign: "center", fontSize: "1.8rem", fontWeight: 700, marginBottom: 12 }}>18 PII Detection Patterns</h2>
          <p style={{ textAlign: "center", color: "#6b6b8a", marginBottom: 32, fontSize: ".9rem" }}>Hardened regex engine — zero false negatives across our internal suite of 185 test cases.</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 }}>
            {piiTypes.map((t) => (
              <div key={t} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, padding: "10px 14px", fontSize: ".85rem", color: "#c4c4d4" }}>
                {t}
              </div>
            ))}
          </div>
        </section>

        {/* ── Compliance Frameworks ────────────────────────────────────── */}
        <section style={{ maxWidth: 1100, margin: "3rem auto", padding: "0 2rem" }}>
          <h2 style={{ textAlign: "center", fontSize: "1.8rem", fontWeight: 700, marginBottom: 32 }}>7 Built-In Compliance Frameworks</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
            {compliance.map((c) => (
              <div key={c.name} style={{ background: "rgba(196,162,101,0.05)", border: "1px solid rgba(196,162,101,0.15)", borderRadius: 12, padding: "1.25rem" }}>
                <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "#c4a265", marginBottom: 4 }}>{c.name}</div>
                <div style={{ fontSize: ".8rem", color: "#6b6b8a" }}>{c.desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── How It Works ─────────────────────────────────────────────── */}
        <section style={{ maxWidth: 900, margin: "4rem auto", padding: "0 2rem" }}>
          <h2 style={{ textAlign: "center", fontSize: "1.8rem", fontWeight: 700, marginBottom: 40 }}>How It Works</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 24 }}>
            {[
              { step: "1", title: "Subscribe", desc: "Choose your plan below and complete checkout via Stripe." },
              { step: "2", title: "Get Your Key", desc: "Receive your AL-XXXX license key by email within seconds." },
              { step: "3", title: "Download & Install", desc: "Download Aditya Lens for Windows and run the installer." },
              { step: "4", title: "Activate & Scan", desc: "Paste your key in Settings → License. Start scanning instantly." },
            ].map((s) => (
              <div key={s.step} style={{ textAlign: "center" }}>
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: "linear-gradient(135deg, #c4a265, #d4b878)", color: "#08081a", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "1.2rem", margin: "0 auto 12px" }}>{s.step}</div>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>{s.title}</div>
                <div style={{ fontSize: ".85rem", color: "#6b6b8a", lineHeight: 1.5 }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Pricing ──────────────────────────────────────────────────── */}
        <section id="pricing" style={{ maxWidth: 1200, margin: "4rem auto", padding: "0 2rem" }}>
          <h2 style={{ textAlign: "center", fontSize: "2rem", fontWeight: 700, marginBottom: 8 }}>Simple, Transparent Pricing</h2>
          <p style={{ textAlign: "center", color: "#6b6b8a", marginBottom: 8, fontSize: ".9rem" }}>All plans include free updates. Cancel anytime. Prices in USD.</p>
          <p style={{ textAlign: "center", color: "#8b8ba0", marginBottom: 20, fontSize: ".75rem" }}>All prices in USD. Applicable taxes will be added at checkout.</p>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginBottom: 32 }}>
            <span style={{ color: !annual ? "#c4a265" : "#6b6b8a", fontWeight: !annual ? 700 : 400, fontSize: ".9rem" }}>Monthly</span>
            <button onClick={() => setAnnual(!annual)} style={{ width: 52, height: 28, borderRadius: 14, border: "1px solid rgba(196,162,101,0.3)", background: annual ? "linear-gradient(135deg, #c4a265, #d4b878)" : "#1a1a2e", cursor: "pointer", position: "relative" }}>
              <span style={{ position: "absolute", top: 4, left: annual ? 26 : 4, width: 18, height: 18, borderRadius: "50%", background: annual ? "#08081a" : "#6b6b8a", transition: "left 0.3s" }} />
            </button>
            <span style={{ color: annual ? "#c4a265" : "#6b6b8a", fontWeight: annual ? 700 : 400, fontSize: ".9rem" }}>Annual <span style={{ color: "#22c55e", fontSize: ".75rem", fontWeight: 700 }}>Save 2 months</span></span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20, marginBottom: 24 }}>
            {plans.map((plan) => (
              <div key={plan.name} style={{ background: "rgba(255,255,255,0.03)", border: plan.popular ? "2px solid #c4a265" : "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: "2rem", position: "relative", display: "flex", flexDirection: "column" }}>
                {plan.popular && (
                  <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: "linear-gradient(135deg, #c4a265, #d4b878)", color: "#08081a", padding: "4px 16px", borderRadius: 12, fontSize: ".7rem", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>
                    Most Popular
                  </div>
                )}
                <div style={{ fontSize: "1.1rem", fontWeight: 700, marginBottom: 4 }}>{plan.name}</div>
                <div style={{ marginBottom: 12 }}>
                  <span style={{ fontSize: "2.5rem", fontWeight: 800, color: "#c4a265" }}>&pound;{annual ? plan.annualPrice.toLocaleString() : plan.price}</span>
                  <span style={{ fontSize: ".9rem", color: "#6b6b8a" }}>{annual ? "/year" : "/month"}</span>
                  {annual && <div style={{ color: "#22c55e", fontSize: ".75rem", fontWeight: 600, marginTop: 2 }}>Save &pound;{(plan.price * 12 - plan.annualPrice).toLocaleString()}/yr</div>}
                </div>
                <p style={{ fontSize: ".85rem", color: "#8b8ba0", lineHeight: 1.5, marginBottom: 16, minHeight: 44 }}>{plan.desc}</p>
                <div style={{ fontSize: ".75rem", color: "#6b6b8a", marginBottom: 16 }}>{plan.devices} device{plan.devices > 1 ? "s" : ""}</div>
                <ul style={{ listStyle: "none", padding: 0, margin: "0 0 24px", flex: 1 }}>
                  {plan.features.map((f) => (
                    <li key={f} style={{ fontSize: ".85rem", color: "#c4c4d4", padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.04)", display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <span style={{ color: "#c4a265", fontWeight: 700, flexShrink: 0 }}>&#10003;</span> {f}
                    </li>
                  ))}
                </ul>
                <a href={PAYMENT_LINKS[plan.tier as keyof typeof PAYMENT_LINKS]} target="_blank" rel="noopener noreferrer" style={{ display: "block", textAlign: "center", background: plan.popular ? "linear-gradient(135deg, #c4a265, #d4b878)" : "transparent", color: plan.popular ? "#08081a" : "#c4a265", border: plan.popular ? "none" : "1px solid #c4a265", padding: "12px", borderRadius: 10, fontWeight: 700, fontSize: ".9rem", textDecoration: "none" }}>
                  {plan.cta}
                </a>
              </div>
            ))}
          </div>

          {/* Enterprise Card */}
          <div style={{ background: "linear-gradient(135deg, rgba(196,162,101,0.08), rgba(196,162,101,0.03))", border: "2px solid rgba(196,162,101,0.25)", borderRadius: 16, padding: "2.5rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40, alignItems: "center" }}>
            <div>
              <div style={{ display: "inline-block", background: "linear-gradient(135deg, #c4a265, #d4b878)", color: "#08081a", padding: "4px 12px", borderRadius: 8, fontSize: ".7rem", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 12 }}>Enterprise</div>
              <div style={{ marginBottom: 12 }}>
                <span style={{ fontSize: "2.5rem", fontWeight: 800, color: "#c4a265" }}>&pound;{annual ? enterprise.annualPrice.toLocaleString() : enterprise.price.toLocaleString()}</span>
                <span style={{ fontSize: ".9rem", color: "#6b6b8a" }}>{annual ? "/year" : "/month"}</span>
                {annual && <div style={{ color: "#22c55e", fontSize: ".75rem", fontWeight: 600 }}>Save &pound;{(enterprise.price * 12 - enterprise.annualPrice).toLocaleString()}/yr</div>}
              </div>
              <p style={{ fontSize: ".9rem", color: "#8b8ba0", lineHeight: 1.6, marginBottom: 16 }}>{enterprise.desc}</p>
              <div style={{ fontSize: ".8rem", color: "#6b6b8a", marginBottom: 20 }}>{enterprise.devices} devices / seats</div>
              <a href={PAYMENT_LINKS.enterprise} target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", background: "linear-gradient(135deg, #c4a265, #d4b878)", color: "#08081a", padding: "14px 32px", borderRadius: 10, fontWeight: 700, fontSize: ".95rem", textDecoration: "none" }}>
                Get Enterprise
              </a>
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {enterprise.features.map((f) => (
                <li key={f} style={{ fontSize: ".85rem", color: "#c4c4d4", padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.04)", display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <span style={{ color: "#c4a265", fontWeight: 700, flexShrink: 0 }}>&#10003;</span> {f}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ── Security Banner ──────────────────────────────────────────── */}
        <section style={{ maxWidth: 900, margin: "4rem auto", padding: "2.5rem", textAlign: "center", background: "rgba(196,162,101,0.05)", border: "1px solid rgba(196,162,101,0.15)", borderRadius: 16 }}>
          <h3 style={{ fontSize: "1.3rem", fontWeight: 700, marginBottom: 12 }}>Your Data Never Leaves Your Machine</h3>
          <p style={{ color: "#6b6b8a", lineHeight: 1.7, fontSize: ".9rem", maxWidth: 600, margin: "0 auto" }}>
            Aditya Lens runs entirely on your Windows PC. All file scanning, PII detection, redaction, and AI analysis happen locally.
            No files are uploaded. No data is transmitted. No telemetry. The only network call is a one-time license validation.
          </p>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────────────── */}
        <section id="faq" style={{ maxWidth: 750, margin: "4rem auto", padding: "0 2rem 4rem" }}>
          <h2 style={{ textAlign: "center", fontSize: "1.8rem", fontWeight: 700, marginBottom: 32 }}>Frequently Asked Questions</h2>
          {faqs.map((faq, i) => (
            <div key={i} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", marginBottom: 0 }}>
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                style={{ width: "100%", background: "transparent", border: "none", color: "#e6edf3", padding: "16px 0", fontSize: ".95rem", fontWeight: 600, textAlign: "left", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                {faq.q}
                <span style={{ color: "#c4a265", fontSize: "1.2rem", transform: openFaq === i ? "rotate(45deg)" : "none", transition: "transform 0.2s" }}>+</span>
              </button>
              {openFaq === i && (
                <p style={{ color: "#8b8ba0", fontSize: ".85rem", lineHeight: 1.7, padding: "0 0 16px", margin: 0 }}>
                  {faq.a}
                </p>
              )}
            </div>
          ))}
        </section>

        {/* ── CTA ──────────────────────────────────────────────────────── */}
        <section style={{ textAlign: "center", padding: "4rem 2rem", background: "linear-gradient(135deg, rgba(196,162,101,0.08), transparent)" }}>
          <h2 style={{ fontSize: "2rem", fontWeight: 700, marginBottom: 12 }}>Ready to Protect Your Data?</h2>
          <p style={{ color: "#6b6b8a", marginBottom: 24, fontSize: ".9rem" }}>Start scanning for PII in minutes. No cloud. No risk.</p>
          <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
            <a href="#pricing" style={{ background: "linear-gradient(135deg, #c4a265, #d4b878)", color: "#08081a", padding: "14px 32px", borderRadius: 10, fontWeight: 700, textDecoration: "none" }}>
              Choose Your Plan
            </a>
            <a href="mailto:support@adityalabs.ai" style={{ border: "1px solid rgba(255,255,255,0.1)", color: "#c4c4d4", padding: "14px 32px", borderRadius: 10, fontWeight: 600, textDecoration: "none" }}>
              Contact Sales
            </a>
          </div>
        </section>

      </div>
      <Footer />
    </>
  );
}
